import React, { type ReactNode, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { formatPrice, Product } from "../types";
import { CurrencyAmount } from "@/components/CurrencyAmount";
import { BookingLanguage } from "@/types/booking";
import { processMarkdown } from "./SafeMarkdown";
import { Check, X, Calendar } from "lucide-react";
import { ProductUspsModal } from "@/components/product-step/card/ProductUSPs.tsx";
import { createProductBadges } from "@/components/product-step/utils/createBadge.ts";
import { resolveProductKey } from "@/locales";
import { languageMap } from "@/i18n";
import { CareIcon } from "@/components/icons/CareIcon";
import { ActionPlanIcon } from "@/components/icons/ActionPlanIcon";
import { MriScannerIcon } from "@/components/icons/MriScannerIcon";
import { CirclePlusIcon } from "@/components/icons/CirclePlusIcon";
import { InfoIcon } from "@/components/icons/InfoIcon";
import { useMarket } from "@/hooks/useMarket";
import { marketHasImaging } from "@/config/market";
import { useMembership } from "@/contexts/MembershipContext";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import { MembershipDetailsModal } from "@/components/product-step/card/MembershipDetailsModal";
import { absorbsMembershipFee } from "@/utils/bloodMembershipPricing";
import { isFollowupBloodTest } from "@/utils/bloodTestVisibility";
import { DsButton, NudgeArrow } from "@/components/ui/ds-button";
import {
  ValueAreas,
  ValueBenefits,
  ValueCarousel,
} from "@/components/product-step/card/ValueFraming";

export interface PackageCardProps {
  product: Product;
  isSelected: boolean;
  onSelect: () => void;
  language: BookingLanguage;
  onSelectAndContinue?: () => void;
  isOnlyProduct?: boolean;
  allProducts?: Product[];
  /** Enable the wide horizontal two-column layout for blood-only cards.
   *  Only set when the card is in the 780px single-product container. */
  horizontalLayout?: boolean;
  /** Optional badge rendered at the very top of the card (above the title row).
   *  Used by experiment wrappers to inject a coloured tier icon. */
  headerBadge?: ReactNode;
  /** Pick this package with a membership attached. Kept separate from
   *  `onSelectAndContinue` because that one deliberately CLEARS membership
   *  state — a plain pick is a package-led cart. */
  onSelectAsMember?: () => void;
  /** Show member-price strikethrough even when the user isn't a member yet.
   *  Used by the membership-first MRI cross-sell, where the user has already
   *  committed to the membership, so the scan should preview its member price. */
  forceMemberPricing?: boolean;
}

export const PackageCard: React.FC<PackageCardProps> = ({
  product,
  isSelected,
  onSelect,
  language,
  onSelectAndContinue,
  onSelectAsMember,
  isOnlyProduct = false,
  allProducts = [],
  horizontalLayout = false,
  headerBadge,
  forceMemberPricing = false,
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  // Markets without imaging (DE, NL) — the third feature icon must not
  // reference MRI.
  const { market } = useMarket();
  const noImaging = !marketHasImaging(market);
  const [showDescription, setShowDescription] = useState(false);
  const [showMembershipInfo, setShowMembershipInfo] = useState(false);
  const infoClickedRef = useRef(false);
  const { getEffectivePrice, isMember } = useMembership();
  const { tiers: membershipTiers } = useMembershipTiers();

  // Preview the member rate to non-members on scan packages: the price exists,
  // but today it's only discoverable after they've already committed to a
  // membership, which is the wrong order. Members and the membership-first
  // cross-sell already see the member price as the headline, so this line
  // would just repeat it for them.
  //
  // `includes_blood_draw` is the real gate, not a nicety. The membership's
  // first year is only absorbed by scans that carry a blood test
  // (useBundleSummary → membershipFirstYearIncluded), so those are the only
  // ones where "book as a member for X" charges exactly X. On a scan without
  // one — Core — the cadence is billed on top, and the line would quote a
  // price we don't take. That also matches the product call not to put member
  // pricing on Core at all.
  const listAmount = product.default_price?.unit_amount ?? null;
  const memberOnlyAmount = product.member_price?.unit_amount ?? null;
  const showMemberPreview =
    absorbsMembershipFee(product) &&
    !isMember &&
    !forceMemberPricing &&
    listAmount !== null &&
    memberOnlyAmount !== null &&
    memberOnlyAmount < listAmount;

  // Access USP data — structure comes from CMS so we treat it as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uspData: any =
    product.usps?.booking_flow_first_page?.data ||
    product.usps?.booking_flow_first_page?.[language] ||
    null;

  // Get translated name and description
  const displayName =
    product.translations?.name || resolveProductKey(language, product.name);
  const description =
    product.translations?.description ||
    resolveProductKey(language, product.description);
  const safeDescription = processMarkdown(description || "");

  const handleInfoClick = () => {
    infoClickedRef.current = true;
    setShowDescription(true);
    setTimeout(() => {
      infoClickedRef.current = false;
    }, 100);
  };

  // "Book as a member": the cart becomes membership + this scan at the member
  // rate, then the flow moves on so the summary shows both. This replaced the
  // old add-ons-step upsell card, and fires at the moment the user is actually
  // looking at the price.
  const bookAsMember = () => {
    if (onSelectAsMember) onSelectAsMember();
    else if (onSelectAndContinue) onSelectAndContinue();
    else onSelect();
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (infoClickedRef.current) {
      return;
    }
    // Only make full card clickable on mobile (< lg breakpoint)
    if (window.innerWidth >= 1024) return;
    // Clicking card navigates to next step (select + continue)
    if (onSelectAndContinue) {
      onSelectAndContinue();
    } else {
      onSelect();
    }
  };

  // Skip if not a package product
  if (product.product_type !== "package") return null;

  const formatDuration = (minutes: number) => {
    if (minutes > 90) {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      if (remainingMins === 0) {
        return `${hours} ${t(hours > 1 ? "packageCard.hours" : "packageCard.hour")}`;
      }
      return t("packageCard.duration", { hours, minutes: remainingMins });
    }
    return `${minutes} ${t("packageCard.minutesShort")}`;
  };

  const isBloodOnly = product.package_types?.includes("blood-only");
  // AHD-14079: NL experiment — the single blood card leads with what the
  // customer gets (benefits, pictures, the areas it covers).
  const valueLayout = market === "nl" && isBloodOnly && horizontalLayout;
  // ponytail: the experiment quotes the draw itself (15 min) rather than the
  // catalog's visit length; move to product.duration_minutes if it sticks.
  const durationMinutes = valueLayout ? 15 : product.duration_minutes;
  const durationText = durationMinutes
    ? formatDuration(durationMinutes)
    : null;

  // Blood tests + MRI Advanced get the aubergine `primary` button; MRI Core
  // and Pro get the navy `primary-alt`. Decided here so the conic-gradient
  // wrapper below can also key off `isAdvanced`.
  const isAdvanced = displayName.toLowerCase().includes("advanced");

  // No default border - border is only shown when selected via the conic gradient wrapper
  const cardBorderClass = "";

  // Get the tier badge text (Essentials for Core, Best value for blood tests)
  const getTierBadge = () => {
    if (isBloodOnly && !isFollowupBloodTest(product.stripe_product_id)) {
      return t("packageCard.bestValue");
    }
    const nameLower = displayName.toLowerCase();
    if (nameLower.includes("core")) {
      return t("packageCard.essentials");
    }
    return null;
  };
  const tierBadge = getTierBadge();

  // Render checklist items from USP data
  const renderChecklist = () => {
    if (!uspData) return null;

    // Support both new format (checklist array) and old format (sections with items)
    let allItems: Array<{
      id: string;
      icon: string;
      labelKey?: string;
      label?: string;
      hasAiBadge?: boolean;
    }> = [];

    if (uspData.checklist && Array.isArray(uspData.checklist)) {
      // New simplified format: direct checklist array
      allItems = uspData.checklist;
    } else {
      // Old format: sections with nested items
      const sectionsKey = Object.keys(uspData).find((key) =>
        Array.isArray(uspData[key]),
      );
      if (!sectionsKey) return null;
      const sections = uspData[sectionsKey];

      sections.forEach((section: { items?: unknown[] }) => {
        if (section.items) {
          section.items.forEach((item) => {
            allItems.push(
              item as {
                id: string;
                icon: string;
                labelKey?: string;
                label?: string;
                hasAiBadge?: boolean;
              },
            );
          });
        }
      });
    }

    if (allItems.length === 0) return null;

    // Blood-only horizontal layout already surfaces the last 4 generic
    // benefits (reviewed by Swiss physicians, action plan, add MRI, report)
    // in the feature-icons row above — drop the tail to avoid duplication.
    const visibleItems = isBloodOnly
      ? allItems.slice(0, Math.max(0, allItems.length - 4))
      : allItems;

    return (
      <div className="flex flex-col">
        {visibleItems.map((item) => {
          const isIncluded = item.icon === "check";
          return (
            <div key={item.id} className="flex items-start px-2 gap-2 py-1.5">
              {isIncluded ? (
                /* Solid deep-green disc with a white tick. Used across both
                   the blood-only and full-body checkup cards, and the same
                   green as the member price above and the ticks in the
                   package comparison table. */
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                  className="flex-shrink-0 mt-1"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7.5"
                    className="fill-deep-green-600"
                  />
                  <path
                    d="M5 8l2 2 4-4"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <div
                  className="w-4 h-4 rounded-full bg-neutral-50 flex items-center justify-center flex-shrink-0 mt-1"
                  style={{ boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.04)" }}
                >
                  <X
                    className="w-2.5 h-2.5 text-neutral-300"
                    strokeWidth={2.5}
                  />
                </div>
              )}
              <span
                className={`text-[14px] leading-[24px] ${isIncluded ? "text-neutral-900" : "text-neutral-400"}`}
              >
                {resolveProductKey(language, item.labelKey || item.label)}
                {item.hasAiBadge && isIncluded && (
                  <svg
                    width="26"
                    height="20"
                    viewBox="0 0 26 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="ml-1.5 inline-block align-middle"
                  >
                    <path
                      d="M0 10C0 4.47715 4.47715 0 10 0H16C21.5228 0 26 4.47715 26 10C26 15.5228 21.5228 20 16 20H10C4.47715 20 0 15.5228 0 10Z"
                      fill="url(#paint0_radial_ai_badge)"
                    />
                    <path
                      d="M8.48527 14L11.1053 7H12.4753L15.0853 14H13.8553L13.1953 12.27H10.3253L9.67527 14H8.48527ZM10.6453 11.29H12.8753L11.7553 8.24L10.6453 11.29ZM15.8031 7H16.9531V14H15.8031V7Z"
                      fill="#48214A"
                    />
                    <defs>
                      <radialGradient
                        id="paint0_radial_ai_badge"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientTransform="matrix(-33.5 44.375 -22.4211 -26.4474 30 -18.75)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#D8CAFF" />
                        <stop offset="0.615857" stopColor="#F1F0FF" />
                        <stop offset="1" stopColor="#D7DFFF" />
                      </radialGradient>
                    </defs>
                  </svg>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Feature icons row — layout/sizing aligned with MembershipCard's pillar row
  // so blood-only and membership cards read as the same component family.
  // Icon choices stay distinct per main: CareIcon, target rings (ActionPlanIcon),
  // MRI scanner for blood-only / Dr. Erat photo for the full-body variant.
  const renderFeatureIcons = () => {
    return (
      <div className="flex gap-2">
        <div className="flex flex-1 flex-col items-center gap-1 px-1 pt-0 pb-0 text-center md:pb-2">
          <div className="flex size-10 items-center justify-center text-neutral-500">
            <CareIcon />
          </div>
          <p className="text-[12px] leading-[1.2] font-normal text-neutral-700 whitespace-pre-line">
            {t("packageCard.insightsBySwissPhysicians")}
          </p>
        </div>
        <div className="flex flex-1 flex-col items-center gap-1 px-1 pt-0 pb-0 text-center md:pb-2">
          <div className="flex size-10 items-center justify-center text-neutral-500">
            <ActionPlanIcon size={28} />
          </div>
          <p className="text-[12px] leading-[1.2] font-normal text-neutral-700 whitespace-pre-line">
            {t("packageCard.personalActionPlan")}
          </p>
        </div>
        <div className="flex flex-1 flex-col items-center gap-1 px-1 pt-0 pb-0 text-center md:pb-2">
          <div className="flex size-10 items-center justify-center text-neutral-500">
            {isBloodOnly ? (
              // Markets without imaging: swap the MRI scanner for an
              // "add tests" plus.
              noImaging ? (
                <CirclePlusIcon size={28} />
              ) : (
                <MriScannerIcon size={28} />
              )
            ) : (
              <img
                src="/advisory/erat.webp"
                alt="Dr. Anna Erat"
                className="size-10 object-cover rounded-full"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
          </div>
          <p className="text-[12px] leading-[1.2] font-normal text-neutral-700 whitespace-pre-line">
            {isBloodOnly
              ? noImaging
                ? t("packageCard.addTestsAnytime")
                : t("packageCard.addTestsOrMri")
              : t("packageCard.videoConsultWithDoctor")}
          </p>
        </div>
      </div>
    );
  };

  const infoButton = (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        handleInfoClick();
      }}
      className="cursor-pointer"
      aria-label={t("common.showDescription")}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 22 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="11" cy="11" r="11" fill="#F4F4F4" />
        <path
          d="M10.77 6.42C11.08 6.42 11.34 6.52 11.54 6.73C11.74 6.93 11.84 7.19 11.84 7.5C11.84 7.81 11.74 8.07 11.54 8.27C11.34 8.48 11.08 8.58 10.77 8.58C10.46 8.58 10.2 8.48 10 8.27C9.8 8.07 9.7 7.81 9.7 7.5C9.7 7.19 9.8 6.93 10 6.73C10.2 6.52 10.46 6.42 10.77 6.42ZM9.94 9.94H11.6V15.91H9.94V9.94Z"
          fill="#919094"
        />
      </svg>
    </button>
  );

  const topSectionContent = (
    <>
      {/* Badge row — info button only inline for the vertical (non-blood)
          layout; in blood-only horizontal mode it floats to the card's
          top-right corner instead. */}
      <div className="flex flex-row justify-between items-start h-[22px]">
        <div>
          {!valueLayout && (product.is_popular || tierBadge) && (
            <Badge
              className={`text-[11px] px-4 py-0.5 rounded-[100px] font-medium border-0 ${
                isBloodOnly
                  ? "bg-neutral-50 hover:bg-neutral-50 text-neutral-900"
                  : product.is_popular
                    ? "bg-deep-green-100 hover:bg-deep-green-100 text-deep-green-700"
                    : "bg-neutral-100 hover:bg-neutral-100 text-neutral-900"
              }`}
            >
              {isBloodOnly
                ? tierBadge
                : product.is_popular
                  ? t("packageCard.popular")
                  : tierBadge}
            </Badge>
          )}
        </div>
        {!isBloodOnly && infoButton}
      </div>

      {/* Title + duration + description + price */}
      <div className="flex flex-col px-1 flex-grow">
        {/* Package name - mt-2 (8px) from badge row */}
        <h3 className="font-medium text-neutral-900 mt-2 text-[28px] leading-[36px]">
          {displayName}
        </h3>

        {/* Duration info line - mt-2 (8px) from title, same as badge→title.
            14px, not 12: it was the smallest type on the card for no reason,
            and the full line still clears one row with room to spare. */}
        <div className="flex flex-row flex-wrap items-center gap-1 mt-2">
          {durationText && (
            <>
              <span className="text-[14px] leading-[130%] text-neutral-500">
                {t("packageCard.durationLabel")}: {durationText}
              </span>
              {!isBloodOnly && (
                <>
                  <span className="text-[14px] leading-[130%] text-neutral-500">
                    ·
                  </span>
                  <span className="text-[14px] leading-[130%] text-neutral-500">
                    {t("packageCard.noRadiation")}
                  </span>
                </>
              )}
            </>
          )}
        </div>

        {valueLayout ? (
          <ValueBenefits language={language} />
        ) : (
          <div className="mt-2">
            <p className="text-neutral-900 text-[14px] leading-[20px]">
              {safeDescription}
            </p>
          </div>
        )}

        {/* Price - mt-auto pushes to bottom of flex container, ensures alignment across cards */}
        <div className="mt-auto pt-2">
          {product.default_price?.unit_amount ? (
            (() => {
              const originalAmount = product.default_price.unit_amount;
              // Both branches mean "this person is on member pricing", so both
              // quote the member price on every package, Core included. The
              // packages-page teaser one step above is the only Core-specific
              // suppression, and it is gated separately on showMemberPreview.
              const memberAmount = isMember
                ? getEffectivePrice(product)
                : forceMemberPricing
                  ? (product.member_price?.unit_amount ?? originalAmount)
                  : originalAmount;
              const hasDiscount =
                (isMember || forceMemberPricing) &&
                memberAmount < originalAmount;
              return (
                <div className="text-[22px] leading-[130%] text-neutral-900">
                  <CurrencyAmount
                    market={market}
                    symbolClassName="text-[14px] font-medium tracking-[0.1px]"
                  >
                    {hasDiscount && (
                      <span className="text-neutral-500 line-through">
                        {formatPrice(originalAmount, market, true)}
                      </span>
                    )}{" "}
                    <span className="font-bold">
                      {formatPrice(memberAmount, market, true)}
                    </span>
                  </CurrencyAmount>
                </div>
              );
            })()
          ) : (
            <span className="text-neutral-500 italic">
              {t("packageCard.priceOnRequest")}
            </span>
          )}

          {/* Secondary line, deliberately not the headline: the list price is
              what this booking charges today. The member rate is folded into
              the action itself, in one link that costs a third of the width a
              separate label plus a parenthetical did.
              It quotes the SAVING, not the member price: a second absolute
              number next to the headline makes the reader do the subtraction
              themselves, and the difference is the part that argues for the
              membership (Ferdinand's note on AHD-8140). The ⓘ opens the same
              details modal the blood cards use. */}
          {showMemberPreview && (
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[14px] leading-[20px] text-neutral-600">
              <button
                type="button"
                onClick={(e) => {
                  infoClickedRef.current = true;
                  e.preventDefault();
                  e.stopPropagation();
                  bookAsMember();
                  setTimeout(() => {
                    infoClickedRef.current = false;
                  }, 100);
                }}
                className="text-left text-[14px] font-normal leading-[20px] normal-case text-deep-green-600 underline decoration-deep-green-600/40 underline-offset-2 transition-colors hover:text-deep-green-700 hover:decoration-deep-green-700"
              >
                <Trans
                  t={t}
                  i18nKey="memberPrice.bookAsMemberSave"
                  values={{
                    amount: `${(product.default_price.currency || "CHF").toUpperCase()} ${formatPrice(
                      listAmount - memberOnlyAmount,
                      market,
                      true,
                    )}`,
                  }}
                  components={[<span className="font-semibold" />]}
                />
              </button>

              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  // The whole card selects on click — don't book a scan just
                  // because someone wanted to read what membership is.
                  infoClickedRef.current = true;
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMembershipInfo(true);
                  setTimeout(() => {
                    infoClickedRef.current = false;
                  }, 100);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMembershipInfo(true);
                  }
                }}
                aria-label={t("membershipUpsell.moreInfo")}
                className="inline-flex size-5 cursor-pointer items-center justify-center rounded-full"
              >
                {/* The app's filled-grey glyph, same as every other ⓘ in the
                    flow — lucide's outline Info was a one-off here. */}
                <InfoIcon size={16} />
              </span>
            </p>
          )}
        </div>
      </div>

      <MembershipDetailsModal
        isOpen={showMembershipInfo}
        onClose={() => setShowMembershipInfo(false)}
        language={language}
        membershipTiers={membershipTiers}
      />

      {/* CTA Button — DS Button (primary aubergine for blood/Advanced, navy
          primary-alt for Core/Pro) with the nudge-arrow hover treatment. */}
      <div className="pt-6">
        <DsButton
          variant={isBloodOnly || isAdvanced ? "primary" : "primary-alt"}
          className="group w-full"
          iconRight={<NudgeArrow />}
          onClick={(e) => {
            e.stopPropagation();
            if (onSelectAndContinue) {
              onSelectAndContinue();
            } else {
              onSelect();
            }
          }}
        >
          {t("packageCard.selectPackage", { productName: displayName })}
        </DsButton>
      </div>
    </>
  );

  const card = (
    <div
      className="p-[1px] rounded-[21px] h-full relative"
      style={{
        background:
          product.is_popular && !isAdvanced
            ? "conic-gradient(from 180deg at 50% 50%, #A099F0 -1.33deg, #94AEFB 13.25deg, #D5BAFF 140.07deg, #9482FF 211.41deg, #BC6DD7 353.48deg, #A099F0 358.67deg, #94AEFB 373.25deg)"
            : "transparent",
      }}
    >
      <div
        className={`${isBloodOnly ? "p-5 lg:p-8" : "p-3 pb-6"} transition-shadow duration-300 ease-out relative rounded-[20px] flex flex-col h-full lg:cursor-default cursor-pointer shadow-card hover:shadow-card-hover ${cardBorderClass}`}
        style={{
          /* Same surface as MembershipCard — 95% white, white stroke, soft
             blue-gray shadow tinted to match the page palette (no muddy
             grey cast). */
          background: "rgba(255, 255, 255, 0.95)",
          border: "1px solid #ffffff",
        }}
        onClick={handleCardClick}
      >
        {/* Info button — pinned to the card's top-right corner in the
            blood-only horizontal layout so the eye sits at the very edge
            instead of beside the badge in the left column. */}
        {isBloodOnly && (
          <div className="absolute top-3 right-3 z-10">{infoButton}</div>
        )}
        {/* Main content wrapper with 24px gap between major sections */}
        <div
          className={`flex flex-col h-full ${isBloodOnly ? "gap-4" : "gap-6"}`}
        >
          {isBloodOnly && horizontalLayout ? (
            /* Stay vertical until lg — matches the wrapper's `lg:min-w-[780px]`
               breakpoint in PackageList, so the right column never gets
               squeezed enough to wrap checklist items onto two lines. When
               stacked, `gap-6` mirrors the MembershipCard's vertical rhythm
               between the CTA half and the features half. */
            <div className="flex flex-col gap-6 lg:flex-row lg:gap-8 flex-grow">
              <div className="flex flex-col lg:w-[320px] lg:shrink-0 lg:border-r lg:border-black/[0.06] lg:pr-8">
                {topSectionContent}
              </div>
              <div className="flex-1 flex flex-col gap-4">
                {valueLayout ? (
                  <ValueCarousel language={language} />
                ) : (
                  <>
                    {renderFeatureIcons()}
                    {renderChecklist()}
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              {headerBadge && <div className="mb-2">{headerBadge}</div>}
              <div className="flex flex-col flex-grow">{topSectionContent}</div>
              {renderFeatureIcons()}
              {renderChecklist()}
            </>
          )}
        </div>

        <ProductUspsModal
          product={product}
          productName={displayName}
          productId={product.id}
          language={language}
          isOpen={showDescription}
          onClose={() => setShowDescription(false)}
          onSelectAndContinue={onSelectAndContinue}
        />
      </div>
    </div>
  );

  return valueLayout ? (
    <div className="flex flex-col gap-6">
      {card}
      <ValueAreas language={language} />
    </div>
  ) : (
    card
  );
};
